import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchDraftOrderById, updateDraftOrderLineItems, undoDeclineDraftOrder } from '../api/draftOrders';
import { getToken } from '../utils/auth';
import Loading from '../components/other/Loading';
import Error from '../components/other/Error';
import RequestPortalLink, { isExpiredPortalTokenError } from '../components/other/RequestPortalLink';

import DraftOrderHeader from '../components/other/DraftOrderHeader';
import LineItem from '../components/other/LineItem';
import ModalMain from '../components/modals/ModalMain';
import ModalAction from '../components/modals/ModalAction';
import ModalShareQuote from '../components/modals/ModalShareQuote';
import { useDraftOrderActionModals } from '../hooks/useDraftOrderActionModals';
import { useExpirationStatus } from '../hooks/useExpirationStatus';
// import TaxCertUploader from '../components/TaxCertUploader';

import { RESTRICTED_VENDORS, canEditQuantity, customerCanPayByPo, getPairedProductId } from '../utils/constants';
import { formatCurrency, formatPhoneNumber } from '../utils/formatting';

import { openGorgiasChat, klavityoPortalItems } from '../utils/utils';
import { addLeadScore, computeTimeBucketAndPoints, LEAD_SCORE_WEIGHTS } from '../utils/leadScoring';
import { getCookie, setCookie, trackKlaviyoEvent, pushDataLayerEvent } from '../../utils/utils';


import { pdf } from '@react-pdf/renderer';
import DraftOrderDetailPDF from '../pdf/DraftOrderDetailPDF';

import { PdfIcon, TaxIcon } from '../../utils/icons';
import { BsSend } from "react-icons/bs";
import { FaAngleLeft } from 'react-icons/fa6';

const ACTIONS_TOP_COOKIE = 'quote_portal_actions_top';
const ACTIONS_TOP_COOKIE_DAYS = 50;
const UPDATE_REQUESTED_TAG = 'Update Requested';

/** Match server lockout flags after a customer line-item edit. */
function withLineItemEditLockout(order) {
  if (!order) return order;

  const tags = [...(order.tags || [])];
  if (!tags.includes(UPDATE_REQUESTED_TAG)) tags.push(UPDATE_REQUESTED_TAG);
  if (!tags.includes('portal-updates-requested')) tags.push('portal-updates-requested');

  return {
    ...order,
    tags,
    needsUpdates: true,
    requestedUpdates: true,
  };
}

/** Same behavior as the Download PDF control; used by deep link ?open_pdf=1 */
function openDraftOrderPdf(pdfUrl, pdfTitle) {
  if (!pdfUrl || typeof window === 'undefined') return;
  if (window.openPdfInModal) {
    window.openPdfInModal(pdfUrl, encodeURIComponent(pdfTitle || ''));
  } else {
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  }
}

function DraftOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [draftOrder, setDraftOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalContent, setModalContent] = useState(null);
  
  const [removedItemIndices, setRemovedItemIndices] = useState(new Set());
  const [quantityChanges, setQuantityChanges] = useState({});
  const [customAttributesChanges, setCustomAttributesChanges] = useState({});
  // const [editingAttribute, setEditingAttribute] = useState(null); // { itemIndex, key, value }
  const [isSaving, setIsSaving] = useState(false);
  const [quantityLoadingIndex, setQuantityLoadingIndex] = useState(null);
  const quantityLoadingTimeoutRef = useRef(null);
  const [expandedAttributes, setExpandedAttributes] = useState(new Set());
  const [undoDeclining, setUndoDeclining] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Render the React-PDF document to a blob and open it in the modal/new tab,
  // reusing the same viewer as the hosted quote PDF link.
  const viewGeneratedPdf = useCallback(async () => {
    if (!draftOrder || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const blob = await pdf(
        <DraftOrderDetailPDF draftOrder={draftOrder} shop={{ customerEmail: 'fun@willygoat.com' }} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      openDraftOrderPdf(url, `willygoat-quote-${draftOrder.name}`);
    } catch (e) {
      console.error('Failed to generate PDF', e);
    } finally {
      setGeneratingPdf(false);
    }
  }, [draftOrder, generatingPdf]);

  // Calculate expiration status using custom hook
  const { expired, expirationColorClass, expirationText, expirationTextClass } = useExpirationStatus(draftOrder?.expirationDate);

  // A/B test: optional `actions_top=1` URL param renders the action buttons
  // above the header in addition to their normal spot below. We check both
  // page-level (?actions_top=1#/...) and route-level (#/...?actions_top=1)
  // query strings because we're behind a HashRouter — page-level params survive
  // hash navigations automatically; route-level ones don't. A cookie keeps the
  // variant sticky if a follow-up link drops the param.
  const showActionsAtTop = useMemo(() => {
    const isOptedIn = (search) => {
      const v = new URLSearchParams(search).get('actions_top');
      return v === '1' || v === 'true' || v === 'yes';
    };
    if (typeof window !== 'undefined' && isOptedIn(window.location.search)) return true;
    if (isOptedIn(location.search)) return true;
    return getCookie(ACTIONS_TOP_COOKIE) === '1';
  }, [location.search]);

  useEffect(() => {
    if (showActionsAtTop) {
      setCookie(ACTIONS_TOP_COOKIE, '1', ACTIONS_TOP_COOKIE_DAYS);
    }
  }, [showActionsAtTop]);

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  useEffect(() => () => {
    if (quantityLoadingTimeoutRef.current) {
      clearTimeout(quantityLoadingTimeoutRef.current);
    }
  }, []);

  const loadOrder = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const { token } = getToken();
      
      if (!token) {
        setError('No authentication token found. Please access this page from your email link.');
        if (!silent) setLoading(false);
        return null;
      }

      const data = await fetchDraftOrderById(orderId, token);
      setDraftOrder(data.draftOrder);
      return data.draftOrder;
    } catch (err) {
      console.error('Error loading quote:', err);
      setError(err.message || 'Failed to load quote');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Append quote name to URL so front-end JS can read it (e.g. new URLSearchParams(location.search).get('quote_name'))
  // Prefer the associated order name when available, otherwise fall back to the draft order name.
  useEffect(() => {
    const quoteName = draftOrder?.order?.name || draftOrder?.name;
    if (!quoteName) return;
    const params = new URLSearchParams(location.search);
    if (params.get('quote_name') === quoteName) return;
    params.set('quote_name', quoteName);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [draftOrder?.order?.name, draftOrder?.name, location.pathname, navigate]);

  // ?open_pdf=1 before the hash (share links) or on the hash route query. Stripped after handling.
  // sessionStorage avoids double-open under React Strict Mode remounts.
  useEffect(() => {
    if (!draftOrder || !orderId) return;

    const pageParams = new URLSearchParams(window.location.search);
    const routeParams = new URLSearchParams(location.search);
    const raw = pageParams.get('open_pdf') || routeParams.get('open_pdf');
    const wantsOpen = raw === '1' || raw === 'true' || raw === 'yes';
    if (!wantsOpen) return;

    const stripPageOpenPdf = () => {
      if (!pageParams.has('open_pdf')) return;
      pageParams.delete('open_pdf');
      const next = pageParams.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`;
      window.history.replaceState(null, '', url);
    };

    const stripRouteOpenPdf = () => {
      if (!routeParams.has('open_pdf')) return;
      routeParams.delete('open_pdf');
      const next = routeParams.toString();
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
    };

    const stripOpenPdfParam = () => {
      stripPageOpenPdf();
      stripRouteOpenPdf();
    };

    const dedupeKey = `portal_open_pdf_${orderId}`;
    if (sessionStorage.getItem(dedupeKey)) {
      stripOpenPdfParam();
      return;
    }
    sessionStorage.setItem(dedupeKey, '1');

    // Generate the PDF client-side (same as the "View PDF" button) and open it.
    viewGeneratedPdf();
    stripOpenPdfParam();
  }, [draftOrder, orderId, location.pathname, location.search, navigate, viewGeneratedPdf]);

  const showModal = (content) => {
    setModalContent(content);
  };

  const closeModal = () => {
    setModalContent(null);
  };

  const { handleApprove, handleDecline, handleRequestUpdates, handleRequestExtension, handleAskQuestion } = useDraftOrderActionModals({
    showModal,
    orderId: draftOrder?.id,
    canPayByPo: customerCanPayByPo(draftOrder?.customer?.tags),
    quoteName: draftOrder?.name || draftOrder?.order?.name,
    customerEmail: draftOrder?.customer?.email,
    onComplete: (action) => {
      if (action === 'declined') {
        loadOrder();
        return;
      }
      const delay = 4000;
      setTimeout(() => navigate('/'), delay);
    },
  });

  const handleUndoDecline = async () => {
    const { token } = getToken();
    if (!token || !orderId) return;
    try {
      setUndoDeclining(true);
      setError(null);
      await undoDeclineDraftOrder(orderId, token);
      await loadOrder();
      showModal(
        <ModalAction
          mode="success"
          title="Decline removed"
          message="This quote is open again while it is still valid."
        />
      );
      pushDataLayerEvent('quote_portal_undo_decline', { order_id: orderId });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to undo decline');
    } finally {
      setUndoDeclining(false);
    }
  };

  const handleAskQuestionClick = () => {
    const draftOrderId = draftOrder?.id || draftOrder?.order?.id;
    const customerEmail = draftOrder.customer?.email;
    const quoteId = draftOrder?.order?.name || draftOrder?.name;

    // If Gorgias opens successfully, count it as an engagement signal.
    const scoreDelta = LEAD_SCORE_WEIGHTS.questionAsked;
    const leadScore = addLeadScore(draftOrderId, scoreDelta, 'question_asked_gorgias_opened');

    pushDataLayerEvent('quote_portal_ask_question', { 
      order_id: draftOrderId, 
      customer_email: customerEmail,
      lead_score: leadScore,
      lead_score_delta: scoreDelta,
    });
    // const prefill = draftOrder?.name || draftOrder?.order?.name ? `I have a question about ${draftOrder.order?.name ? `order ${draftOrder.order?.name}` : `quote ${draftOrder?.name}` }` : '';
    const prefill = `I have a question about ${draftOrder.order?.name ? `order ${draftOrder.order?.name}` : `quote ${draftOrder?.name}` }`;
    openGorgiasChat(prefill).then((opened) => {
      if (!opened) {
        handleAskQuestion();
        return;
      }

      pushDataLayerEvent('quote_portal_ask_question_gorgias_opened', {
        order_id: draftOrderId,
        customer_email: customerEmail,
        quote_name: quoteId,
        lead_score: leadScore,
        lead_score_delta: scoreDelta,
      });

      trackKlaviyoEvent('Quote Portal Question Asked', {
        DraftOrderId: draftOrderId,
        QuoteID: quoteId,
        customer_email: customerEmail,
        Source: 'gorgias_opened',
        LeadScore: leadScore,
        LeadScoreDelta: scoreDelta,
      });
    });
  };

  

  

  const canRemoveItem = (item) => {
    // Cannot remove items with product type "Miscellaneous Fees"
    if (item.productType === 'Miscellaneous Fees' || item.productType === 'Shades Addon') {
      return false;
    }
    return true;
  };

  const handleRemoveItem = (index) => {
    const item = draftOrder.lineItems[index];
    if (!canRemoveItem(item)) {
      return;
    }
    pushDataLayerEvent('quote_portal_remove_item', { order_id: orderId, item: item });

    const productId = item?.productLegacyResourceId ?? item?.productId;
    const pairedProductId = getPairedProductId(productId);
    const pairedIdx = pairedProductId != null
      ? draftOrder.lineItems.findIndex((li) => String(li?.productLegacyResourceId ?? li?.productId ?? '') === pairedProductId)
      : -1;

    setRemovedItemIndices(prev => {
      const newSet = new Set(prev);
      const isAdding = !newSet.has(index);
      if (isAdding) {
        newSet.add(index);
        if (pairedIdx >= 0 && canRemoveItem(draftOrder.lineItems[pairedIdx])) {
          newSet.add(pairedIdx);
        }
      } else {
        newSet.delete(index);
        if (pairedIdx >= 0) newSet.delete(pairedIdx);
      }
      return newSet;
    });
  };

  const handleQuantityChange = (index, newQuantity) => {
    const item = draftOrder.lineItems[index];
    if (!canEditQuantity(item)) {
      return;
    }

    const quantity = parseInt(newQuantity, 10);
    if (isNaN(quantity) || quantity < 1) return;

    pushDataLayerEvent('quote_portal_quantity_change', { order_id: orderId, item: item, newQuantity: quantity });

    setQuantityLoadingIndex(index);
    if (quantityLoadingTimeoutRef.current) {
      clearTimeout(quantityLoadingTimeoutRef.current);
    }
    quantityLoadingTimeoutRef.current = setTimeout(() => {
      setQuantityLoadingIndex((current) => (current === index ? null : current));
      quantityLoadingTimeoutRef.current = null;
    }, 400);

    const originalQuantity = draftOrder.lineItems[index].quantity;
    
    setQuantityChanges(prev => {
      const updated = { ...prev };
      if (quantity === originalQuantity) {
        delete updated[index];
      } else {
        updated[index] = quantity;
      }
      return updated;
    });
  };

  const handleUpdateCustomAttribute = (index, key, value) => {
    setCustomAttributesChanges(prev => {
      const updated = { ...prev };
      if (!updated[index]) {
        updated[index] = [...(draftOrder.lineItems[index].customAttributes || [])];
      }
      
      const attrIndex = updated[index].findIndex(attr => attr.key === key);
      if (attrIndex >= 0) {
        if (value === null || value === undefined) {
          // Remove attribute if value is null/undefined
          updated[index].splice(attrIndex, 1);
        } else {
          // Update existing attribute
          updated[index][attrIndex] = { key, value };
        }
      } else if (value !== null && value !== undefined) {
        // Add new attribute
        updated[index].push({ key, value });
      }
      
      return updated;
    });
  };

  const handleRemoveCustomAttribute = (index, key) => {
    handleUpdateCustomAttribute(index, key, null);
  };

  // const handleClearCustomAttributes = (index) => {
  //   setCustomAttributesChanges(prev => ({
  //     ...prev,
  //     [index]: []
  //   }));
  // };

  const handleSaveChanges = async () => {
    if (removedItemIndices.size === 0 && Object.keys(quantityChanges).length === 0 && Object.keys(customAttributesChanges).length === 0) return;

    // Filter out removed items and apply quantity and customAttributes changes
    // const remainingItems = draftOrder.lineItems
    //   .filter((_, idx) => !removedItemIndices.has(idx))
    //   .map((item, idx) => ({
    //     variantId: item.variantId,
    //     title: item.title,
    //     quantity: quantityChanges[idx] || item.quantity,
    //     price: item.price,
    //     customAttributes: customAttributesChanges[idx] !== undefined ? customAttributesChanges[idx] : item.customAttributes
    //   }));

    const remainingItems = draftOrder.lineItems
      .map((item, originalIdx) => ({ item, originalIdx }))
      .filter(({ originalIdx }) => !removedItemIndices.has(originalIdx))
      .map(({ item, originalIdx }) => ({
        variantId: item.variantId,
        title: item.title,
        quantity: quantityChanges[originalIdx] ?? item.quantity,
        price: item.price,
        customAttributes:
          customAttributesChanges[originalIdx] !== undefined
            ? customAttributesChanges[originalIdx]
            : item.customAttributes,
      }));

    pushDataLayerEvent('quote_portal_save_changes', { order_id: orderId, remainingItems: remainingItems });

    if (remainingItems.length === 0) {
      showModal(
        <ModalAction
          mode="success"
          title="Cannot Remove All Items"
          message="You must keep at least one item in the quote. If you want to decline the entire quote, please use the Decline button."
        />
      );
      return;
    }

    try {
      setIsSaving(true);
      const { token } = getToken();
      
      // Build comment describing changes
      const changes = [];
      if (removedItemIndices.size > 0) {
        changes.push(`removed ${removedItemIndices.size} item${removedItemIndices.size > 1 ? 's' : ''}`);
      }
      if (Object.keys(quantityChanges).length > 0) {
        changes.push(`updated ${Object.keys(quantityChanges).length} quantity${Object.keys(quantityChanges).length > 1 ? 'ies' : ''}`);
      }
      // if (Object.keys(customAttributesChanges).length > 0) {
      //   changes.push(`modified ${Object.keys(customAttributesChanges).length} item attribute${Object.keys(customAttributesChanges).length > 1 ? 's' : ''}`);
      // }
      const comment = `Customer ${changes.join(', ')}`;
      
      await updateDraftOrderLineItems(orderId, remainingItems, comment, token);
      pushDataLayerEvent('quote_portal_save_changes_success', { order_id: orderId, remainingItems: remainingItems, comment: comment });

      setRemovedItemIndices(new Set());
      setQuantityChanges({});
      setDraftOrder((prev) => withLineItemEditLockout(prev));

      const freshOrder = await loadOrder({ silent: true });
      setDraftOrder((prev) => withLineItemEditLockout(freshOrder ?? prev));
      
      showModal(
        <ModalAction
          mode="success"
          title="Changes Saved"
          message="Your changes have been saved successfully. The quote has been updated."
        />
      );
    } catch (err) {
      console.error('Error saving changes:', err);
      showModal(
        <ModalAction
          mode="success"
          title="Error"
          message={err.message || 'Failed to save changes. Please try again.'}
        />
      );
    } finally {
      setIsSaving(false);
      setQuantityLoadingIndex(null);
      if (quantityLoadingTimeoutRef.current) {
        clearTimeout(quantityLoadingTimeoutRef.current);
        quantityLoadingTimeoutRef.current = null;
      }
    }
  };

  const handleCancelChanges = () => {
    setRemovedItemIndices(new Set());
    setQuantityChanges({});
    pushDataLayerEvent('quote_portal_cancel_changes', { order_id: orderId });
  };

  const toggleAttributesExpanded = (index) => {
    pushDataLayerEvent('quote_portal_toggle_attributes_expanded', { order_id: orderId, index: index });
    setExpandedAttributes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const getSortedLineItems = () => {
    if (!draftOrder?.lineItems) return [];
    
    return draftOrder.lineItems
      .map((item, originalIdx) => ({ item, originalIdx }))
      .sort((a, b) => {
        const aRestricted = RESTRICTED_VENDORS.includes(a.item.vendor);
        const bRestricted = RESTRICTED_VENDORS.includes(b.item.vendor);
        
        // Non-restricted items come first
        if (aRestricted && !bRestricted) return 1;
        if (!aRestricted && bRestricted) return -1;
        return 0;
      });
  };


  const hasChanges = removedItemIndices.size > 0 || Object.keys(quantityChanges).length > 0;
  const invoiceSent = draftOrder?.invoiceSentAt != null;
  const canEditLineItems = draftOrder &&
    !(draftOrder.tags || []).includes('Update Requested') &&
    !expired &&
    !draftOrder.approved &&
    !draftOrder.declined &&
    !draftOrder.needsUpdates &&
    invoiceSent;
  const isDeclined = draftOrder && draftOrder.declined;
  const isApproved = draftOrder && draftOrder.approved;
  // const isUpdatesRequested = draftOrder && (draftOrder.tags || []).includes('portal-updates-requested');
  const canMakeChanges = !isDeclined || !isApproved || (canEditLineItems && draftOrder.status !== 'COMPLETED');
  
  const remainingItemCount = draftOrder?.lineItems
    ? draftOrder.lineItems.length - removedItemIndices.size
    : 0;
  const canSaveWithoutRemovingAll = remainingItemCount > 0;

  const hasTrackedViewRef = useRef(false);
  const hasTrackedTimeSpentRef = useRef(false);
  const timeTrackingInitializedRef = useRef(false);
  const activeTimeStartTsRef = useRef(null);
  const accumulatedActiveTimeMsRef = useRef(0);

  // Reset timer state when navigating between different quotes.
  useEffect(() => {
    hasTrackedTimeSpentRef.current = false;
    timeTrackingInitializedRef.current = false;
    activeTimeStartTsRef.current = null;
    accumulatedActiveTimeMsRef.current = 0;
  }, [orderId]);

  useEffect(() => {
    if (!draftOrder || hasTrackedViewRef.current) return;

    const learnq = window._learnq || [];
    const customer = draftOrder.customer;

    const identifyPayload = {
      ...(customer?.email && { $email: customer.email }),
      ...(customer?.firstName && { $first_name: customer.firstName }),
      ...(customer?.lastName && { $last_name: customer.lastName }),
      ...(customer?.phone && { $phone_number: customer.phone }),
    };
    if (Object.keys(identifyPayload).length > 0) {
      learnq.push(['identify', identifyPayload]);
    }

    const leadScore = addLeadScore(draftOrder.id, LEAD_SCORE_WEIGHTS.viewed, 'viewed_base');

    const klaviyo_portal_viewed = {
      $value: Number(draftOrder.totalPrice),
      customer_properties: {
        ...(customer?.email && { $email: customer.email }),
        ...(customer?.firstName && { $first_name: customer.firstName }),
        ...(customer?.lastName && { $last_name: customer.lastName }),
        ...(customer?.phone && { $phone_number: customer.phone }),
      },
      QuoteExpiration: draftOrder.expirationDate || undefined,
      OrderId: draftOrder.order?.name || draftOrder.name,
      QuoteID: draftOrder.name,
      ItemNames: draftOrder.lineItems.map((item) => item.title),
      Brands: draftOrder.lineItems.map((item) => item.vendor),
      Items: JSON.stringify(klavityoPortalItems(draftOrder.lineItems)),
      LeadScore: leadScore,
    };
    learnq.push(['track', 'Quote Portal Viewed', klaviyo_portal_viewed]);

    pushDataLayerEvent('quote_portal_detail_view', {
      orderId: draftOrder.id,
      customerEmail: draftOrder.customer?.email,
      lead_score: leadScore,
    });

    hasTrackedViewRef.current = true;
  }, [draftOrder]);

  // Track "time on quote page" and flush once when leaving/unloading.
  useEffect(() => {
    if (!draftOrder) return;
    if (timeTrackingInitializedRef.current) return;

    timeTrackingInitializedRef.current = true;
    activeTimeStartTsRef.current = Date.now();

    const stopActiveTimer = () => {
      if (activeTimeStartTsRef.current == null) return;
      accumulatedActiveTimeMsRef.current += Date.now() - activeTimeStartTsRef.current;
      activeTimeStartTsRef.current = null;
    };

    const startActiveTimer = () => {
      if (activeTimeStartTsRef.current != null) return;
      activeTimeStartTsRef.current = Date.now();
    };

    const handleVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') stopActiveTimer();
      if (document.visibilityState === 'visible') startActiveTimer();
    };

    const flushTimeSpent = () => {
      if (hasTrackedTimeSpentRef.current) return;
      hasTrackedTimeSpentRef.current = true;

      // Finalize current active segment (if any).
      stopActiveTimer();

      const timeSeconds = Math.max(
        0,
        Math.round(accumulatedActiveTimeMsRef.current / 1000)
      );

      const { timeBucket, timePoints } = computeTimeBucketAndPoints(timeSeconds);
      const leadScore = addLeadScore(draftOrder.id, timePoints, 'time_points_applied');

      pushDataLayerEvent('quote_portal_time_on_page', {
        orderId: draftOrder.id,
        customerEmail: draftOrder.customer?.email,
        quote_value: Number(draftOrder.totalPrice),
        time_seconds: timeSeconds,
        time_bucket: timeBucket,
        lead_score: leadScore,
      });

      // Klaviyo event for scoring.
      const learnq = window._learnq || [];
      const klaviyo_time_spent = {
        $value: Number(draftOrder.totalPrice),
        QuoteExpiration: draftOrder.expirationDate || undefined,
        OrderId: draftOrder.order?.name || draftOrder.name,
        QuoteID: draftOrder.name,
        TimeOnPageSeconds: timeSeconds,
        TimeOnPageBucket: timeBucket,
        LeadScore: leadScore,
      };
      learnq.push(['track', 'Quote Portal Time Spent', klaviyo_time_spent]);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', flushTimeSpent);
    window.addEventListener('beforeunload', flushTimeSpent);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flushTimeSpent);
      window.removeEventListener('beforeunload', flushTimeSpent);
      flushTimeSpent();
    };
  }, [draftOrder]);

  // Renders the action buttons (Approve / Decline / Request Updates / etc.) in
  // either the `bottom` (default) or `top` placement. Both placements render
  // the same buttons so the A/B test only varies position; the `--top` /
  // `--bottom` modifier class lets us tweak styling per placement in CSS.
  const renderActions = (placement) => (
    <div className={`draft-order-actions draft-order-actions--${placement}`}>
      {expired && draftOrder.status !== 'COMPLETED' ? (
        <>
          <button
            className="button button--secondary button--bordered"
            onClick={handleRequestExtension}
            disabled={hasChanges}
          >
            Request Extension
          </button>

          {draftOrder.status === 'COMPLETED' && (
            <button
              className="button button--danger button--bordered"
              // onClick={handleAskQuestion}
              onClick={handleAskQuestionClick}
              disabled={hasChanges}
            >
              Ask a Question
            </button>
          )}

          <button
            className="button button--share button--bordered"
            onClick={() => showModal(
              <ModalShareQuote className="share-quote-modal" title="Share Quote" orderId={orderId} token={getToken().token} quoteName={draftOrder.name} onClose={closeModal} />
            )}
          >
            Share Quote
          </button>
        </>
      ) : (
        <>
          {draftOrder.declined &&
            !expired &&
            (draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE') && (
              <button
                type="button"
                className="button button--secondary button--bordered"
                onClick={handleUndoDecline}
                disabled={hasChanges || undoDeclining}
              >
                {undoDeclining ? 'Updating…' : 'Undo Decline'}
              </button>
          )}

          {!draftOrder.approved && !draftOrder.declined && (
            <>
              {canEditLineItems && (draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE') && (
                <button
                  className="button button--primary button--bordered"
                  onClick={handleApprove}
                  disabled={hasChanges}
                >
                  Approve Quote
                </button>
              )}
              {canEditLineItems && draftOrder.status !== 'COMPLETED' && (
                <button
                  className="button button--secondary button--bordered"
                  onClick={handleRequestUpdates}
                  disabled={hasChanges}
                >
                  Request Updates
                </button>
              )}
              {canEditLineItems && (draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE') && (
                <button
                  className="button button--danger button--bordered"
                  onClick={handleDecline}
                  disabled={hasChanges}
                >
                  Decline
                </button>
              )}
            </>
          )}
          {(draftOrder.status === 'COMPLETED' || draftOrder.approved || draftOrder.declined) && (
            <button
              className="button button--secondary button--bordered"
              // onClick={handleAskQuestion}
              onClick={handleAskQuestionClick}
              disabled={hasChanges}
            >
              Ask a Question
            </button>
          )}
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="app">
        {/* <Header /> */}
        <Loading />
      </div>
    );
  }

  if (error) {
    const { token } = getToken();
    if (isExpiredPortalTokenError(error)) {
      return (
        <div className="app">
          <RequestPortalLink expiredToken={token} draftOrderId={orderId} />
        </div>
      );
    }
    
    return (
      <div className="app">
        {/* <Header /> */}
        <Error message={error} />
      </div>
    );
  }

  if (!draftOrder) {
    return (
      <div className="app">
        {/* <Header /> */}
        <Error message="Quote not found" />
      </div>
    );
  }

  return (
    
      <main className="detail-page">
          <div className="back-button-container">
            <button className="back-button" onClick={() => navigate('/')}>
              <FaAngleLeft />&nbsp;Back to All Quotes
            </button>
          </div>
          
          {/* <PageIntro message="You're ready to get the playground party started! Thanks for trusting WillyGoat with your order. To preserve any special discount applied to this quote, please plan to pay via Check or ACH." link="https://www.willygoat.com/pages/ach-information" linkText="Request ACH information here." /> */}

          <div className={`draft-order detail-view draft-${draftOrder.status.toLowerCase()}`} data-quote-number={draftOrder.name}>

            {showActionsAtTop && renderActions('top')}

            <DraftOrderHeader
              draftOrder={draftOrder}
              expired={expired}
              expirationColorClass={expirationColorClass}
              expirationText={expirationText}
            />
            

            <div className="draft-order-header draft-order-customer">
              <div className="customer-name">
                <div className="customer-name-inner">
                  <strong>{draftOrder.customer.firstName} {draftOrder.customer.lastName}</strong>
                  {draftOrder.customer.email && (
                    <p className="customer-email">{draftOrder.customer.email}</p>
                  )}
                  {draftOrder.customer.phone && (
                    <p className="customer-phone">{formatPhoneNumber(draftOrder.customer.phone)}</p>
                  )}

                  {draftOrder.taxExempt ? (
                    <span className="pill-button pill-button-tax-exempt">
                      Tax Exempt
                    </span>
                  ) : null}

                  {draftOrder.order?.statusPageUrl && (
                    <p><a href={draftOrder.order?.statusPageUrl} target="_blank" rel="noopener noreferrer">
                      View Order Status
                    </a></p>
                  )}

                  {/* {!draftOrder.taxExempt && (
                    <div className="tax-cert-upload-link">
                      <button
                        type="button"
                        className="button button--secondary button--bordered button--small"
                        onClick={() =>
                          showModal(
                            <TaxCertUploader
                              showCard={false}
                              showTitle={false}
                              showDescription={false}
                              title="Upload Tax Exemption Certificate"
                              className="tax-cert-modal"
                            />
                          )
                        }
                      >
                        Upload Tax Exemption
                      </button>
                    </div>
                  )} */}
                </div>
              </div>

              <div className="customer-address">
                {/* <strong>{draftOrder.shippingAddress.name}</strong> */}
                <strong>Shipping Address:</strong>
                <p>
                { draftOrder.shippingAddress.name && (
                  <span>{draftOrder.shippingAddress.name}<br /></span>
                )}
                { draftOrder.shippingAddress.company && (
                  <span>{draftOrder.shippingAddress.company}<br /></span>
                )}
                { draftOrder.shippingAddress.address1 && (
                  <span>{draftOrder.shippingAddress.address1}<br /></span>
                )}
                { draftOrder.shippingAddress.address2 && (
                  <span>{draftOrder.shippingAddress.address2}<br /></span>
                )}
                { draftOrder.shippingAddress.city && (
                  <span className="customer-address-city">{draftOrder.shippingAddress.city},&nbsp;</span>
                )}
                { draftOrder.shippingAddress.province && (
                  <span className="customer-address-province">{draftOrder.shippingAddress.province}&nbsp;</span>
                )}
                { draftOrder.shippingAddress.zip && (
                  <span className="customer-address-zip">{draftOrder.shippingAddress.zip}</span>
                )}
                </p>
              </div>

              {draftOrder.billingAddress?.address1 && (
                <div className="customer-address customer-address-billing">
                  <strong>Billing Address:</strong>
                  <p>
                    { draftOrder.billingAddress.name && (
                      <span>{draftOrder.billingAddress.name}<br /></span>
                    )}
                    { draftOrder.billingAddress.company && (
                      <span>{draftOrder.billingAddress.company}<br /></span>
                    )}
                    { draftOrder.billingAddress.address1 && (
                      <span>{draftOrder.billingAddress.address1}<br /></span>
                    )}
                    { draftOrder.billingAddress.address2 && (
                      <span>{draftOrder.billingAddress.address2}<br /></span>
                    )}
                    { draftOrder.billingAddress.city && (
                      <span className="customer-address-city">{draftOrder.billingAddress.city},&nbsp;</span>
                    )}
                    { draftOrder.billingAddress.province && (
                      <span className="customer-address-province">{draftOrder.billingAddress.province}&nbsp;</span>
                    )}
                    { draftOrder.billingAddress.zip && (
                      <span className="customer-address-zip">{draftOrder.billingAddress.zip}</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className={`line-items ${hasChanges ? 'line-items--changes' : ''}`}>
              {getSortedLineItems().map(({ item, originalIdx }) => {
                const orderLineItem = draftOrder.order?.lineItems?.find(
                  (li) => li.variantId === item.variantId
                );
                // console.log(orderLineItem, 'orderLineItem', orderLineItem?.unfulfilledQuantity);
                const unfulfilledQuantity = orderLineItem?.unfulfilledQuantity ?? null;
                return (
                  <LineItem
                    draftOrder={draftOrder}
                    key={originalIdx}
                    item={item}
                    index={originalIdx}
                    currency={draftOrder.currency}
                    expandThreshold={5}
                    isExpanded={expandedAttributes.has(originalIdx)}
                    onToggleExpand={toggleAttributesExpanded}
                    variant="detail"
                    isRemoved={removedItemIndices.has(originalIdx)}
                    onRemove={handleRemoveItem}
                    canRemove={canEditLineItems && canRemoveItem(item)}
                    displayQuantity={quantityChanges[originalIdx] !== undefined ? quantityChanges[originalIdx] : item.quantity}
                    onQuantityChange={handleQuantityChange}
                    canEditQuantity={canEditQuantity(item)}
                    canEditLineItems={canEditLineItems}
                    isQuantityLoading={
                      quantityLoadingIndex === originalIdx
                      || (isSaving && quantityChanges[originalIdx] !== undefined)
                    }
                    isOrder={draftOrder.status === 'COMPLETED'}
                    unfulfilledQuantity={unfulfilledQuantity}
                    showManual
                  />
                );
              })}
            </div>

            {hasChanges && canEditLineItems && (
              <div className="changes-notice">
                <p>
                  {removedItemIndices.size > 0 && canSaveWithoutRemovingAll && (
                    <span>You have {removedItemIndices.size} item{removedItemIndices.size > 1 ? 's' : ''} marked for removal. </span>
                  )}
                  {removedItemIndices.size > 0 && !canSaveWithoutRemovingAll && (
                    <span>You cannot remove all items from your quote.</span>
                  )}
                  {Object.keys(quantityChanges).length > 0 && (
                    <span>You have {Object.keys(quantityChanges).length} change{Object.keys(quantityChanges).length > 1 ? 's' : ''}.</span>
                  )}
                </p>
                <div className="changes-actions">
                  <button
                    className={`button button--primary button--bordered${isSaving ? ' button--loading' : ''}`}
                    onClick={handleSaveChanges}
                    disabled={isSaving || !canSaveWithoutRemovingAll}
                    aria-busy={isSaving}
                  >
                    {isSaving && <span className="button__spinner" aria-hidden="true" />}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    className="button button--secondary button--bordered"
                    onClick={handleCancelChanges}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

           



            <div className="total-wrapper">
            
              <div className="order-summary">
                <div className="summary-row">
                  <span className="strong">Subtotal</span>
                  <span className="summary-amount-value">{draftOrder.needsUpdates ? ' (Pending Updates)' : ''} - {formatCurrency(draftOrder.lineItemsSubtotalPrice, draftOrder.currency)}</span>
                </div>
                {parseFloat(draftOrder.totalTax) > 0 && (
                  <div className="summary-row">
                    <span className="strong">Tax</span>
                    <span className="summary-amount-value">{draftOrder.needsUpdates ? ' (Pending Updates)' : ''} - {formatCurrency(draftOrder.totalTax, draftOrder.currency)}</span>
                  </div>
                )}
                {draftOrder.taxLines && draftOrder.taxLines.forEach(line => (
                  <div className="summary-row">
                    <span className="strong">{line.title}</span>
                    <span className="summary-amount-value">{draftOrder.needsUpdates ? ' (Pending Updates)' : ''} - {formatCurrency(line.price, draftOrder.currency)}</span>
                  </div>
                ))}

                
                {parseFloat(draftOrder.appliedDiscount) > 0 && (
                  <div className="summary-row">
                    <span className="strong">Discount {draftOrder.appliedDiscountTitle}</span>
                    <span className={`summary-amount-value ${hasChanges ? 'strike-through' : ''}`}>{draftOrder.needsUpdates ? ' (Pending Updates)' : ''} - ({formatCurrency(draftOrder.appliedDiscount, draftOrder.currency)})</span>
                  </div>
                )}
                {parseFloat(draftOrder.totalShippingPrice2) > 0 && (
                  <div className="summary-row">
                    <span className="strong">{draftOrder.totalShippingName}</span>
                    <span className="summary-amount-value">{draftOrder.needsUpdates ? ' (Pending Updates) - ' : ''}{formatCurrency(draftOrder.totalShippingPrice2, draftOrder.currency)}</span>
                  </div>
                )}

                
                <div className="summary-row total">
                  <span className="strong">Total</span>
                  <span className="summary-amount-value">{draftOrder.needsUpdates ? ' (Pending Updates)' : ''} - {formatCurrency(draftOrder.totalPrice, draftOrder.currency)}</span>
                </div>

                {draftOrder.order?.totalReceived && (
                <div className="summary-row">
                  <span className="strong">Total Received</span>
                  <span className="summary-amount-value">({formatCurrency(draftOrder.order?.totalReceived, draftOrder.currency)})</span>
                </div>
                )}

                {draftOrder.order?.totalOutstanding && (
                <div className="summary-row total">
                  {/* <span className="strong">Total Outstanding</span> */}
                  <span className="strong">Remaining Balance</span>
                  <span className="summary-amount-value">{formatCurrency(draftOrder.order?.totalOutstanding, draftOrder.currency)}</span>
                </div>
                )}

                
              </div>


              
              {renderActions('bottom')}


            </div>
              
            {(canEditLineItems || draftOrder.order?.invoice_pdf) && (
              <div className="order-note">
                {/* {(draftOrder.order?.invoice_pdf || draftOrder.quote_pdf) && ( */}

                  <>
                  {/* <a
                    className="download-pdf-link"
                    href={draftOrder.order?.invoice_pdf || draftOrder.quote_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-quote-number={draftOrder.order?.name || draftOrder.name}
                    onClick={(event) => {
                      event.preventDefault();
                      const pdfUrl = draftOrder.order?.invoice_pdf || draftOrder.quote_pdf;
                      const pdfTitle = draftOrder.order?.name || draftOrder.name;
                      if (window.openPdfInModal && pdfUrl) {
                        window.openPdfInModal(pdfUrl, encodeURIComponent(pdfTitle));
                      } else if (pdfUrl) {
                        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    Download PDF <PdfIcon />
                  </a> */}

                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); viewGeneratedPdf(); }}
                    >
                    {generatingPdf ? 'Preparing…' : 
                      <>View PDF <PdfIcon />
                    </>}
                  </a>
                  </>

                {/* )} */}

                

                {/* {!draftOrder.taxExempt && (
                  <a rel="noopener noreferrer"
                    className="tax-cert-upload-link-button"
                    onClick={() =>
                      showModal(
                        <TaxCertUploader
                          showCard={false}
                          showTitle={true}
                          showDescription={true}
                          title="Upload Tax Exemption"
                          className="tax-cert-modal"
                          message="Upload your tax exemption certificate here."
                        />
                    )}
                  >
                    Upload Tax Exemption
                    <TaxIcon className="taxes-icon" />
                  </a>
                )} */}


                <a rel="noopener noreferrer"
                  onClick={() => showModal(
                    <ModalShareQuote
                      className="share-quote-modal"
                      title={draftOrder.order?.name ? `Share Order ${draftOrder.order?.name}` : `Share Quote ${draftOrder.name}`}
                      orderId={orderId}
                      token={getToken().token}
                      quoteName={draftOrder.name}
                      onClose={closeModal}
                    />
                )}>
                  Share {draftOrder.order?.name ? `Order` : `Quote`}
                  <BsSend className="send-icon" />
                </a>
              </div>
            )}

              

              {/* <PDFDownloadLink
                  document={<DraftOrderDetailPDF draftOrder={draftOrder} shop={{ customerEmail: 'fun@willygoat.com' }} />}
                  fileName={`willygoat-quote-${draftOrder.name}.pdf`}
              >
                {({ loading }) => (loading ? 'Preparing…' : 'Download PDF')}
              </PDFDownloadLink> */}


            {/* 

            <div className="order-summary">
              <div className="summary-row">
                <span className="strong">Subtotal</span>
                <span className="summary-amount-value">{formatCurrency(draftOrder.lineItemsSubtotalPrice, draftOrder.currency)}</span>
              </div>
              {parseFloat(draftOrder.totalTax) > 0 && (
                <div className="summary-row">
                  <span className="strong">Tax</span>
                  <span className="summary-amount-value">{formatCurrency(draftOrder.totalTax, draftOrder.currency)}</span>
                </div>
              )}
              {draftOrder.taxLines && draftOrder.taxLines.forEach(line => (
                <div className="summary-row">
                  <span className="strong">{line.title}</span>
                  <span className="summary-amount-value">{formatCurrency(line.price, draftOrder.currency)}</span>
                </div>
              ))}

              {parseFloat(draftOrder.appliedDiscount) > 0 && (
                <div className="summary-row">
                  <span className="strong">Discount {draftOrder.appliedDiscountTitle}</span>
                  <span className="summary-amount-value">({formatCurrency(draftOrder.appliedDiscount, draftOrder.currency)})</span>
                </div>
              )}
              {parseFloat(draftOrder.totalShippingPrice2) > 0 && (
                <div className="summary-row">
                  <span className="strong">{draftOrder.totalShippingName}</span>
                  <span className="summary-amount-value">{formatCurrency(draftOrder.totalShippingPrice2, draftOrder.currency)}</span>
                </div>
              )}

              
              <div className="summary-row total">
                <span className="strong">Total</span>
                <span className="summary-amount-value">{formatCurrency(draftOrder.totalPrice, draftOrder.currency)}</span>
              </div>
            </div>


            <div className="draft-order-actions">
              {expired && draftOrder.status !== 'COMPLETED' ? (
                <>
                  <button
                    className="button button--secondary button--bordered"
                    onClick={handleRequestExtension}
                    disabled={hasChanges}
                  >
                    Request Extension
                  </button>

                  {draftOrder.status === 'COMPLETED' && (
                    <button
                      className="button button--danger button--bordered"
                      // onClick={handleAskQuestion}
                      onClick={handleAskQuestionClick}
                      disabled={hasChanges}
                    >
                      Ask a Question
                    </button>
                  )}

                  <button
                    className="button button--share button--bordered"
                    onClick={() => showModal(
                      <ModalShareQuote title="Share Quote" orderId={orderId} token={getToken().token} quoteName={draftOrder.name} onClose={closeModal} />
                    )}
                  >
                    Share Quote
                  </button>
                </>
              ) : 
              <>
                {draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE' && (
                  <button
                    className="button button--primary button--bordered"
                    onClick={handleApprove}
                    disabled={hasChanges}
                  >
                    Approve Quote
                  </button>
                )}
                {draftOrder.status !== 'COMPLETED' && (
                  <button
                    className="button button--secondary button--bordered"
                    onClick={handleRequestUpdates}
                    disabled={hasChanges}
                  >
                    Request Updates
                  </button>
                )}
                {draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE' && (
                  <button
                    className="button button--danger button--bordered"
                    onClick={handleDecline}
                    disabled={hasChanges}
                  >
                    Decline
                  </button>
                )}

                {draftOrder.status === 'COMPLETED' && (
                  <button
                    className="button button--secondary button--bordered"
                    // onClick={handleAskQuestion}
                    onClick={handleAskQuestionClick}
                    disabled={hasChanges}
                  >
                    Ask a Question
                  </button>
                )}

                <button
                  className="button button--share button--bordered"
                  onClick={() => showModal(
                    <ModalShareQuote
                      title={draftOrder.order?.name ? `Share Order ${draftOrder.order?.name}` : `Share Quote ${draftOrder.name}`}
                      orderId={orderId}
                      token={getToken().token}
                      quoteName={draftOrder.name}
                      onClose={closeModal}
                    />
                  )}
                >
                  Share {draftOrder.order?.name ? `Order` : `Quote`}
                </button>
                </>
              }
              
            </div>

            
            <div className="order-note">
              {(draftOrder.order?.invoice_pdf || draftOrder.quote_pdf) && (
                <a href={draftOrder.order?.invoice_pdf || draftOrder.quote_pdf} target="_blank" rel="noopener noreferrer">
                  Download PDF <PdfIcon />
                  
                </a>
              )}


              <a rel="noopener noreferrer"
                onClick={() => showModal(
                  <ModalShareQuote
                    title={draftOrder.order?.name ? `Share Order ${draftOrder.order?.name}` : `Share Quote ${draftOrder.name}`}
                    orderId={orderId}
                    token={getToken().token}
                    quoteName={draftOrder.name}
                    onClose={closeModal}
                  />
              )}>
                Share {draftOrder.order?.name ? `Order` : `Quote`}
                <BsSend className="send-icon" />
              </a>
            </div> */}
              


            {/* {draftOrder.customAttributes && (
              <div className="order-note order-custom-attributes">
                {draftOrder.customAttributes.map((attr, idx) => (
                  <div key={idx} className="custom-attribute">
                    <span className="strong">{attr.key}</span>
                    <span>{attr.value}</span>
                  </div>
                ))}
              </div>
            )} */}

            

            
          </div>

          {modalContent && (
            <ModalMain onClose={closeModal} className={modalContent.props?.className}>
              {modalContent}
            </ModalMain>
          )}
      </main>

      
    
  );
}

export default DraftOrderDetailPage;
